import { SearchSelectProps } from 'views/mutual/searchSelector';
import { PageStep } from '../hooks';
import { useEffect, useState } from 'react';
import { useGetAccountDetailInfoQuery } from 'views/utils/graphql/graphql';
import { useGlobalLoadingHandler } from 'views/mutual/globalLoading/hook';
import { useCorporateAccountEstablishStore, useUpdateStore } from 'views/templates/accountOpen/hooks';
import { CorporateAddressConfirmSelectType, useCorporateInfo } from 'views/templates/accountOpen/corporateInfo/hooks';
import {
  AboutRulerType,
  RelastionShipType,
  RulerImportantPublicWorkerType,
  useRulerInfo,
} from 'views/templates/accountOpen/rulerInfo/hooks';
import {
  findCorporateBusinessContentName,
  findSettlementDayName,
  findSettlementMonthName,
  findCorporateLeaseFundPurposeName,
  findInvestorCategoryInfoName,
  findDomesticRealEstatePurposeName,
  findCorporateOverseasRealEstatePurposeName,
  FatcaStatus,
} from 'views/utils/enums';
import { isEmpty } from 'lodash';
import { useTransactionPurpose } from 'views/templates/accountOpen/transactionPurpose/hooks';
import { useDealerInfo } from 'views/templates/accountOpen/dealerInfo/hooks';
import { useRepresentInfo } from 'views/templates/accountOpen/representInfo/hooks';
import { useContactInfo } from 'views/templates/accountOpen/contactInfo/hooks';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useCorporateAccountAdvancedQuestion } from 'views/templates/accountOpen/advanceQuestion/hooks';
import { useInvestorCategoryInfo } from 'views/templates/accountOpen/investorCategoryInfo/hooks';
import { AuthModeSelectType, useTFAuthentication } from 'views/templates/accountOpen/tfAuthentication/hooks';
import { useInvoice } from 'views/templates/accountOpen/invoice/hooks';
import { AccountType } from 'views/templates/module/bankDetailsInput/hooks';
import { useBankInfo } from 'views/templates/accountOpen/bankInfo/hooks';
import { useFatca } from 'views/templates/accountOpen/fatca/hooks';

export enum SelectCorporateItem {
  CorporateInfo = '法人情報',
  TransactionPurpose = '取引目的',
  ContactInfo = '連絡先情報',
  RepresentInfo = '代表者情報',
  DealerInfo = '取引担当者情報',
  RulerInfo = '実質的支配者のご申告',
  InvestorCategoryInfo = '投資家区分情報',
  TFAuthentication = '2段階認証の方法',
  Fatca = '米国納税義務（FATCA）に関する確認',
  BankInfo = '配当金振込用口座（国内不動産）',
  Invoice = 'インボイス番号',
}

let SelectCorporateList: SelectCorporateItem[] = (Object.keys(SelectCorporateItem) as (keyof typeof SelectCorporateItem)[]).map((key) => {
  return SelectCorporateItem[key];
});

// 更新ページ本体
export const useUpdatePage = (
  pageStep: PageStep,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
) => {
  const { showLoading, hideLoading } = useGlobalLoadingHandler();

  const [selected, setSelected] = useState<SelectCorporateItem>(SelectCorporateItem.CorporateInfo);
  // const { add, param, clear } = useUpdateStore();
  const { add, param, clear } = useCorporateAccountEstablishStore();

  const questionInfo = useCorporateAccountAdvancedQuestion({ isUpdate: true, setPageStep });
  const corporateInfo = useCorporateInfo({ isUpdate: true });
  const transactionPurpose = useTransactionPurpose({ isUpdate: true });
  const representInfo = useRepresentInfo({ isUpdate: true });
  const contactInfo = useContactInfo({ isUpdate: true });
  const dealerInfo = useDealerInfo({ isUpdate: true });
  const rulerInfo = useRulerInfo({ isUpdate: true });
  const investorCategoryInfo = useInvestorCategoryInfo({ isUpdate: true });
  const tfAuthentication = useTFAuthentication({ isUpdate: true });
  const invoice = useInvoice({ isUpdate: true });
  const bankInfo = useBankInfo({ isUpdate: true });
  const fatca = useFatca({ isUpdate: true });

  const [accountDetail, getAccountDetail] = useGetAccountDetailInfoQuery({
    variables: {},
    requestPolicy: 'network-only',
    pause: true,
  });

  const [errorItems, setErrorItems] = useState<SelectCorporateItem[]>([]);

  // SMS認証が終わってからアカウント情報を取得する
  // 初期時に取得するとSMS認証しなくてもネットワークを見れば個人情報が見れてしまうので
  useEffect(() => {
    if (pageStep === PageStep.UPDATE && !accountDetail.data) {
      getAccountDetail();
    }
  }, [pageStep]);

  useEffect(() => {
    if (accountDetail.fetching) {
      showLoading();
    } else {
      hideLoading();
    }
    if (!accountDetail.data || !accountDetail.data.GetAccountDetailInfo) return;

    const info = accountDetail.data.GetAccountDetailInfo;

    clear();

    add({
      uniqueId: null,
      dealerQuestion: info.RepresentAndDealerAreSame ?? null,
      identificationQuestion: null, // 要らないが型エラーになるので適当に
      mailAddressQuestion: null,//要らないが型エラーになるので適当に
      selfieQuestion: null, // 要らないが型エラーになるので適当に
      personalInformation: false, // 要らないが型エラーになるので適当に
      generalTermsCondition: false, /// 要らないが型エラーになるので適当に
      confirmResponsibility : info.ConfirmResponsibility ?? false,
      corporateNumber: info.CorporateNumber ?? '',
      corporateName: info.Name ?? '',
      corporateFurigana: info.Furigana ?? '',
      corporatePostCode: info.PostCode ?? '',
      corporatePrefecture: info.Prefecture ?? '',
      corporateCityCountry: info.CityCountry ?? '',
      corporateTownStreetAddress: info.TownStreetAddress ?? '',
      hasNoCorporateTownStreetAddress: info.TownStreetAddress ? false : true,
      corporateBuildingNameAndRoomNumber: info.BuildingNameAndRoomNumber ?? '',
      corporateAddressConfirm: info.CorporateAddressConfirm!
        ? CorporateAddressConfirmSelectType.Same
        : CorporateAddressConfirmSelectType.Different,
      registeredCorporatePostCode: info.RegisteredPostCode ?? '',
      registeredCorporatePrefecture: info.RegisteredPrefecture ?? '',
      registeredCorporateCityCountry: info.RegisteredCityCountry ?? '',
      registeredCorporateTownStreetAddress: info.RegisteredTownStreetAddress ?? '',
      hasNoRegisteredCorporateTownStreetAddress: info.RegisteredTownStreetAddress ? false : true,
      registeredCorporateBuildingNameAndRoomNumber: info.RegisteredBuildingNameAndRoomNumber ?? '',
      corporateSettlementMonth: findSettlementMonthName(info.CorporateSettlementMonth![0]!) ?? '',
      corporateSettlementDay: findSettlementDayName(info.CorporateSettlementDay!) ?? '',
      corporateEstablishmentDate: info.CorporateEstablishmentDate ? new Date(info.CorporateEstablishmentDate) : null,
      corporateMainBusinessContent: findCorporateBusinessContentName(info.CorporateMainBusinessContent!) ?? '',
      corporateMainBusinessContentOtherText: info.CorporateMainBusinessContentOtherText ?? '',
      corporateOtherBusinessContent: findCorporateBusinessContentName(info.CorporateOtherBusinessContent!) ?? '',
      corporateOtherBusinessContentOtherText: info.CorporateOtherBusinessContentOtherText ?? '',
      corporateCapital: (info.CorporateCapital ?? '').toString(),
      corporateNumberOfEmployees: (info.CorporateNumberOfEmployees ?? '').toString(),
      corporateHomepage: info.CorporateHomepage ?? '',
      electronicDocumentAgreement: true, // バリデーションエラー回避
      electronicDocumentAgreementDateTime: '', // 要らないが型エラーになるので適当に
      leaseFundPurpose:
        info.LeaseFundPurpose?.map((x) => {
          return {
            label: findCorporateLeaseFundPurposeName(x!) ?? '',
            checked: true,
          };
        }) ?? [],
      leaseFundPurposeOtherText: info.LeaseFundPurposeOtherText ?? '',
      domesticRealEstatePurpose:
        info.DomesticRealEstatePurpose?.map((x) => {
          return {
            label: findDomesticRealEstatePurposeName(x!) ?? '',
            checked: true,
          };
        }) ?? [],
      domesticRealEstatePurposeOtherText: info.DomesticRealEstatePurposeOtherText ?? '',
      overseasRealEstatePurpose:
        info.OverseasRealEstatePurpose?.map((x) => {
          return {
            label: findCorporateOverseasRealEstatePurposeName(x!) ?? '',
            checked: true,
          };
        }) ?? [],
      overseasRealEstatePurposeOtherText: info.OverseasRealEstatePurposeOtherText ?? '',
      contactPhoneNumber: info.ContactPhoneNumber ?? '',
      contactConfirmPhoneNumber: info.ContactPhoneNumber ?? '',
      contactFaxNumber: info.ContactFaxNumber ?? '',
      representFirstName: info.RepresentFirstName ?? '',
      representLastName: info.RepresentLastName ?? '',
      representFirstNameKana: info.RepresentFirstNameKana ?? '',
      representLastNameKana: info.RepresentLastNameKana ?? '',
      representBirthDay: info.RepresentBirthDay ? new Date(info.RepresentBirthDay) : null,
      representPostCode: info.RepresentPostCode ?? '',
      representPrefecture: info.RepresentPrefecture ?? '',
      representCityCountry: info.RepresentCityCountry ?? '',
      representTownStreetAddress: info.RepresentTownStreetAddress ?? '',
      hasNoRepresentTownStreetAddress: info.RepresentTownStreetAddress ? false : true,
      representBuildingNameAndRoomNumber: info.RepresentBuildingNameAndRoomNumber ?? '',
      representMobilePhoneNumber: info.RepresentMobilePhoneNumber ?? '',
      representPosition: info.RepresentPosition ?? '',
      representMailAddress: info.RepresentMailAddress ?? '',
      representConfirmMailAddress: info.RepresentMailAddress ?? '',
      dealerFirstName: info.DealerFirstName ?? '',
      dealerLastName: info.DealerLastName ?? '',
      dealerFirstNameKana: info.DealerFirstNameKana ?? '',
      dealerLastNameKana: info.DealerLastNameKana ?? '',
      dealerBirthDay: info.DealerBirthDay ? new Date(info.DealerBirthDay) : null,
      dealerPostCode: info.DealerPostCode ?? '',
      dealerPrefecture: info.DealerPrefecture ?? '',
      dealerCityCountry: info.DealerCityCountry ?? '',
      dealerTownStreetAddress: info.DealerTownStreetAddress ?? '',
      hasNoDealerTownStreetAddress: info.DealerTownStreetAddress ? false : true,
      dealerBuildingNameAndRoomNumber: info.DealerBuildingNameAndRoomNumber ?? '',
      dealerMobilePhoneNumber: info.DealerMobilePhoneNumber ?? '',
      dealerMailAddress: info.DealerMailAddress ?? '',
      dealerConfirmMailAddress: info.DealerMailAddress ?? '',
      dealerDepartment: info.DealerDepartment ?? '',
      dealerOtherDepartment: info.DealerOtherDepartment ?? '',
      dealerPosition: info.DealerPosition ?? '',
      aboutRuler1: info.AboutRuler1 ?? AboutRulerType.None,
      addtionalRuler2: info.AboutRuler2 ? true : false,
      addtionalRuler3: info.AboutRuler3 ? true : false,
      ruler1PostCode: info.Ruler1PostCode ?? '',
      ruler1Prefecture: info.Ruler1Prefecture ?? '',
      ruler1CityCountry: info.Ruler1CityCountry ?? '',
      ruler1TownStreetAddress: info.Ruler1TownStreetAddress ?? '',
      hasNoRuler1TownStreetAddress: info.Ruler1TownStreetAddress ? false : true,
      ruler1BuildingNameAndRoomNumber: info.Ruler1BuildingNameAndRoomNumber ?? '',
      ruler1Relationship: info.Ruler1Relationship ?? RelastionShipType.None,
      ruler1Name: info.Ruler1Name ?? '',
      ruler1BirthDay: info.Ruler1BirthDay ? new Date(info.Ruler1BirthDay) : null,
      ruler1MailAddress: info.Ruler1MailAddress ?? '',
      ruler1ImportantPublicWorker:
        info.Ruler1ImportantPublicWorker === null
          ? RulerImportantPublicWorkerType.None
          : info.Ruler1ImportantPublicWorker
          ? RulerImportantPublicWorkerType.Yes
          : RulerImportantPublicWorkerType.No,
      ruler1ConfirmMailAddress: info.Ruler1MailAddress ?? '',
      aboutRuler2: info.AboutRuler2 ?? AboutRulerType.None,
      ruler2PostCode: info.Ruler2PostCode ?? '',
      ruler2Prefecture: info.Ruler2Prefecture ?? '',
      ruler2CityCountry: info.Ruler2CityCountry ?? '',
      ruler2TownStreetAddress: info.Ruler2TownStreetAddress ?? '',
      hasNoRuler2TownStreetAddress: info.Ruler2TownStreetAddress ? false : true,
      ruler2BuildingNameAndRoomNumber: info.Ruler2BuildingNameAndRoomNumber ?? '',
      ruler2Relationship: info.Ruler2Relationship ?? RelastionShipType.None,
      ruler2Name: info.Ruler2Name ?? '',
      ruler2BirthDay: info.Ruler2BirthDay ? new Date(info.Ruler2BirthDay) : null,
      ruler2MailAddress: info.Ruler2MailAddress ?? '',
      ruler2ImportantPublicWorker:
        info.Ruler2ImportantPublicWorker === null
          ? RulerImportantPublicWorkerType.None
          : info.Ruler2ImportantPublicWorker
          ? RulerImportantPublicWorkerType.Yes
          : RulerImportantPublicWorkerType.No,
      ruler2ConfirmMailAddress: info.Ruler2MailAddress ?? '',
      aboutRuler3: info.AboutRuler3 ?? AboutRulerType.None,
      ruler3PostCode: info.Ruler3PostCode ?? '',
      ruler3Prefecture: info.Ruler3Prefecture ?? '',
      ruler3CityCountry: info.Ruler3CityCountry ?? '',
      ruler3TownStreetAddress: info.Ruler3TownStreetAddress ?? '',
      hasNoRuler3TownStreetAddress: info.Ruler3TownStreetAddress ? false : true,
      ruler3BuildingNameAndRoomNumber: info.Ruler3BuildingNameAndRoomNumber ?? '',
      ruler3Relationship: info.Ruler3Relationship ?? RelastionShipType.None,
      ruler3Name: info.Ruler3Name ?? '',
      ruler3BirthDay: info.Ruler3BirthDay ? new Date(info.Ruler3BirthDay) : null,
      ruler3MailAddress: info.Ruler3MailAddress ?? '',
      ruler3ImportantPublicWorker:
        info.Ruler3ImportantPublicWorker === null
          ? RulerImportantPublicWorkerType.None
          : info.Ruler3ImportantPublicWorker
          ? RulerImportantPublicWorkerType.Yes
          : RulerImportantPublicWorkerType.No,
      ruler3ConfirmMailAddress: info.Ruler3MailAddress ?? '',
      investorCategoryInfo:
        info.InvestorCategoryInfo?.map((x) => {
          return {
            label: findInvestorCategoryInfoName(x!) ?? '',
            checked: true,
          };
        }) ?? [],
        investorCategory : param.investorCategory ?? '',
        authMode : info.TwoFactorAuthenticationType ?? AuthModeSelectType.None,
        invoiceNumber: info.InvoiceNumber ?? '',
        hasNoInvoiceNumber : !info.InvoiceNumber,
        bankName: info.TransferBankName ?? '',
        branchName : info.TransferBranchName ?? '',
        accountType : info.TransferAccountType ?? null,
        accountNumber : info.TransferAccountNumber ?? '',
        accountHolderName : info.TransferAccountHolderName ?? '',
        fatca : info.Fatca ?? null,
    });

    SelectCorporateList = SelectCorporateList.filter((x) => !info.RepresentAndDealerAreSame || x !== SelectCorporateItem.RepresentInfo);
  }, [accountDetail]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selected, pageStep]);

  useEffect(() => {
    if (errorItems.length > 0) {
      formErrorScroll();
    }
  }, [errorItems]);

  const onSubmit = async (selected: SelectCorporateItem) => {
    switch (selected) {
      case SelectCorporateItem.CorporateInfo:
        return isEmpty(await corporateInfo.formik.validateForm());
      case SelectCorporateItem.TransactionPurpose:
        return isEmpty(await transactionPurpose.formik.validateForm());
      case SelectCorporateItem.ContactInfo:
        return isEmpty(await contactInfo.formik.validateForm());
      case SelectCorporateItem.RepresentInfo:
        return isEmpty(await representInfo.formik.validateForm());
      case SelectCorporateItem.DealerInfo:
        return isEmpty(await dealerInfo.formik.validateForm());
      case SelectCorporateItem.RulerInfo:
        return isEmpty(await rulerInfo.formik.validateForm());
      case SelectCorporateItem.InvestorCategoryInfo:
        return isEmpty(await investorCategoryInfo.formik.validateForm());
      case SelectCorporateItem.TFAuthentication:
        return isEmpty(await tfAuthentication.formik.validateForm());
      case SelectCorporateItem.Invoice:
        return isEmpty(await invoice.formik.validateForm());
      case SelectCorporateItem.BankInfo:
        return isEmpty(await bankInfo.formik.validateForm());
      case SelectCorporateItem.Fatca:
        return isEmpty(await fatca.formik.validateForm());
      default:
        return true;
    }
  };

  const onSubmitAll = async (): Promise<boolean> => {
    setErrorItems([]);
    const localErrorItems: SelectCorporateItem[] = [];

    for (const item of SelectCorporateList) {
      const isValid = await onSubmit(item);
      if (!isValid) {
        localErrorItems.push(item);
      }
    }
    setErrorItems(localErrorItems);
    return isEmpty(localErrorItems);
  };

  const executeConfirm = async () => {
    const valid = await onSubmitAll();

    if (!valid) {
      return;
    }

    add({
      ...param,
      ...questionInfo.formik.values,
      ...corporateInfo.formik.values,
      ...transactionPurpose.formik.values,
      ...contactInfo.formik.values,
      ...representInfo.formik.values,
      ...dealerInfo.formik.values,
      ...rulerInfo.formik.values,
      ...investorCategoryInfo.formik.values,
      ...tfAuthentication.formik.values,
      ...invoice.formik.values,
      ...bankInfo.formik.values,
      ...fatca.formik.values,
    });

    setPageStep(PageStep.CONFIRM);
  };

  const selectProps: SearchSelectProps = {
    selectedOption: selected,
    setSelectedOption: async (select) => {
      const valid = await onSubmit(selected as SelectCorporateItem);
      if (valid) {
        // バリデーションが通った項目をerrorItemsから削除
        setErrorItems((prevErrorItems) => 
          prevErrorItems.filter((item) => item !== selected)
        );
        setSelected(select as SelectCorporateItem);
      } else {
        formErrorScroll();
      }
    },
    options: SelectCorporateList,
  };

  const templatePageProps = {
    isUpdate: true,
    setPageStep,
  };

  const isMailChanged = (updateMail: string) => {
    return updateMail !== accountDetail!.data!.GetAccountDetailInfo!.DealerMailAddress;
  };

  return {
    selectProps,
    setSelected,
    executeConfirm,
    templatePageProps,
    questionInfo,
    corporateInfo,
    transactionPurpose,
    contactInfo,
    representInfo,
    dealerInfo,
    rulerInfo,
    investorCategoryInfo,
    tfAuthentication,
    invoice,
    bankInfo,
    fatca,
    isMailChanged,
    errorItems,
  };
};
